import type { NoteEntry, Ticket, TicketState } from './types'

/**
 * The rules of a visit's note history, kept out of the component so they can be
 * tested without a browser. The server decides; these only decide what to offer.
 */

/** The states in which a visit is over. Mirrors queue.State.IsTerminal. */
const FINISHED: readonly TicketState[] = ['closed', 'void']

/** Whether this visit can still be given a note: an open one. */
export function acceptsNote(ticket: Pick<Ticket, 'state'>): boolean {
  return !FINISHED.includes(ticket.state)
}

/** Where an entry was written: the desk's name, reception, or "—" when unknown. */
export function notePlace(n: Pick<NoteEntry, 'place' | 'counterName'>, reception: string): string {
  if (n.place === 'desk') return n.counterName || '—'
  if (n.place === 'reception') return reception
  return '—'
}

/** "Desk 1 · Leyla · 10:42": the head line of an entry. */
export function noteHead(n: NoteEntry, reception: string, time: string): string {
  return [notePlace(n, reception), n.author || '—', time].join(' · ')
}

/** Oldest first, as the server lists them; an entry added here joins at the end. */
export function ordered(notes: readonly NoteEntry[]): NoteEntry[] {
  return [...notes].sort((a, b) => Date.parse(a.at) - Date.parse(b.at) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
}

/** Whether to offer Edit and Delete on an entry: the server says it is the
 *  reader's and still at their desk, the screen edits at all, and the visit is open. */
export function mayChange(n: Pick<NoteEntry, 'editable'>, ticket: Pick<Ticket, 'state'>, screenEdits: boolean): boolean {
  return screenEdits && n.editable === true && acceptsNote(ticket)
}

/** What a change to warrants reading the history again: another entry, a delete,
 *  the latest text, the visit closing. */
export function notesKey(t: Pick<Ticket, 'id' | 'note' | 'noteCount' | 'state'>): string {
  return `${t.id}|${t.noteCount ?? 0}|${t.note ?? ''}|${t.state}`
}
